import React, {
    useState,
    useMemo,
    useEffect,
    useRef
} from "react";
import Styles from './styles.module.scss';

import Moment from "react-moment";
import DOMPurify from "dompurify";

import priceIcon from '../../theme/assets/svg/lp_cabinet_project_price_icon.svg';
import saleStartIcon from '../../theme/assets/svg/lp_cabinet_project_sale_start_icon.svg';
import saleEndIcon from '../../theme/assets/svg/lp_cabinet_project_sale_end_icon.svg';
import destributionIcon from '../../theme/assets/svg/lp_cabinet_project_destribution_icon.svg';
import marketIcon from '../../theme/assets/svg/lp_cabinet_project_market_icon.svg';
import circulationIcon from '../../theme/assets/svg/lp_cabinet_project_circulation_icon.svg';

import vestingIcon from '../../theme/assets/svg/lp_cabinet_project_price_icon.svg';
import tgeDateIcon from '../../theme/assets/svg/lp_cabinet_project_price_icon.svg';
import tgeUnlockIcon from '../../theme/assets/svg/lp_cabinet_project_price_icon.svg';
import vestingStartIcon from '../../theme/assets/svg/lp_cabinet_project_price_icon.svg';
import vestingEndIcon from '../../theme/assets/svg/lp_cabinet_project_price_icon.svg';
import linearUnlockIcon from '../../theme/assets/svg/lp_cabinet_project_price_icon.svg';
import { useTranslation } from "react-i18next";

const CabinetProjectDetails = (props) => {
    const { currentProject } = props;
    const { t } = useTranslation();

    const [currentTab, setCurrentTab] = useState('description');
    const [activeDescSection, setActiveDescSection] = useState('highlights');

    const observerRef = useRef(null);

    const sanitizeWithTargetBlank = (html) =>
        DOMPurify.sanitize(html || '').replace(
            /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/gi,
            '<a target="_blank" href=$1$2$1'
        );

    // Все возможные секции описания
    const allDescSections = useMemo(
        () => [
            {
                id: 'highlights',
                field: 'highlights',
                menuLabel: 'Highlights',
                title: t('Основная информация'),
            },
            {
                id: 'problem',
                field: 'problem',
                menuLabel: 'Problem',
                title: t('Нюансы'),
            },
            {
                id: 'solution',
                field: 'solution',
                menuLabel: 'Solution',
                title: t('Решения'),
            },
            {
                id: 'business_model',
                field: 'business_model',
                menuLabel: 'Business Model',
                title: t('Бизнес-модель'),
            },
            {
                id: 'market',
                field: 'market',
                menuLabel: 'Market',
                title: t('Рынок'),
            },
            {
                id: 'investors',
                field: 'investors',
                menuLabel: 'Investors',
                title: t('Инвесторы'),
            },
            {
                id: 'team',
                field: 'team',
                menuLabel: 'Team',
                title: t('Команда'),
            },
            {
                id: 'token_utility',
                field: 'token_utility',
                menuLabel: 'Token Utility',
                title: t('Полезность токена'),
            },
        ],
        [t]
    );

    // Берём только те секции, которые реально существуют в currentProject.description
    const availableDescSections = useMemo(
        () =>
            allDescSections.filter((sec) => {
                const val = currentProject?.description?.[sec.field];
                return typeof val === 'string' && val.trim().length > 0;
            }),
        [allDescSections, currentProject?.description]
    );

    const scrollToSection = (id) => {
        // ✅ CHANGE: не подсвечиваем сразу по клику
        // setActiveDescSection(id);

        const el = document.getElementById(`project-desc-${id}`);
        if (!el) return;

        const headerOffset = 80; // можно подправить под фиксированный хедер
        const elementTop = el.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementTop - headerOffset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
        });
    };

    // 🔥 Scroll Spy
    useEffect(() => {
        if (!availableDescSections.length || currentTab !== 'description') return;

        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver(
            (entries) => {
                // Берём все видимые секции и выбираем ближайшую к верху
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

                if (!visible.length) return;

                const id = visible[0].target.id.replace('project-desc-', '');
                setActiveDescSection(id);
            },
            {
                root: null,
                rootMargin: '-100px 0px -60% 0px',
                threshold: 0.1,
            }
        );

        availableDescSections.forEach((sec) => {
            const el = document.getElementById(`project-desc-${sec.id}`);
            if (el) observerRef.current.observe(el);
        });

        return () => observerRef.current?.disconnect();
    }, [availableDescSections, currentTab]);

    return (
        <section className={Styles.container}>
            <div className={Styles.contentWrap}>
                <div className={Styles.tabRow}>
                    <div
                        className={`${Styles.tabRowItem} ${
                            currentTab === 'description' ? Styles.tabRowItemActive : ''
                        }`}
                        onClick={() => setCurrentTab('description')}
                    >
                        {t('Описание')}
                    </div>
                    <div
                        className={`${Styles.tabRowItem} ${
                            currentTab === 'sale' ? Styles.tabRowItemActive : ''
                        }`}
                        onClick={() => setCurrentTab('sale')}
                    >
                        {t('Продажа токенов')}
                    </div>
                    <div
                        className={`${Styles.tabRowItem} ${
                            currentTab === 'vesting' ? Styles.tabRowItemActive : ''
                        }`}
                        onClick={() => setCurrentTab('vesting')}
                    >
                        {t('Передача прав')}
                    </div>
                </div>

                <div className={Styles.contentBox}>
                    {/* TAB: ОПИСАНИЕ */}
                    {currentTab === 'description' && (
                        <div className={Styles.descriptionWrap}>
                            {/* Левое меню, только если есть хотя бы одна секция */}
                            {availableDescSections.length > 0 && (
                                <div className={Styles.descriptionNav}>
                                    {availableDescSections.map((sec) => (
                                        <button
                                            type="button"
                                            key={sec.id}
                                            className={`${Styles.descriptionNavItem} ${
                                                activeDescSection === sec.id
                                                    ? Styles.descriptionNavItemActive
                                                    : ''
                                            }`}
                                            onClick={() => scrollToSection(sec.id)}
                                        >
                                            {sec.title}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className={Styles.descriptionBox}>
                                {availableDescSections.map((sec) => (
                                    <div
                                        key={sec.id}
                                        id={`project-desc-${sec.id}`}
                                        className={Styles.descriptionBoxItem}
                                    >
                                        <div className={Styles.descriptionBoxItemTitleWrap}>
                                            <div className={Styles.descriptionBoxItemTitle}>
                                                {sec.title}
                                            </div>
                                            <div className={Styles.descriptionBoxItemLine} />
                                        </div>
                                        <div
                                            className={Styles.descriptionBoxItemContent}
                                            dangerouslySetInnerHTML={{
                                                __html: sanitizeWithTargetBlank(
                                                    currentProject?.description?.[sec.field]
                                                ),
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* TAB: ПРОДАЖА */}
                    {currentTab === 'sale' && (
                        <div className={`${Styles.projectWrap} ${Styles.saleWrap}`}>
                            <div className={Styles.titleRow}>{t('Продажа токенов')}</div>
                            <div className={Styles.projectBox}>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img src={priceIcon} alt="Sale Price" />
                                        </div>
                                        <div className={Styles.projectRowName}>{t('Цена продажи')}</div>
                                    </div>
                                    <div className={Styles.projectRowValueWrap}>
                                        {currentProject?.token} = {currentProject?.sale_price * 1} USDT
                                    </div>
                                </div>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img src={saleStartIcon} alt="Sale Start Time" />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Время начала продажи')}
                                        </div>
                                    </div>
                                    <div
                                        className={`${Styles.projectRowValueWrap} ${Styles.projectRowValueWrapWD}`}
                                    >
                                        <div className={Styles.dateTop}>
                                            {currentProject?.sale_opens_at ?
                                                <Moment format="MMMM Do YYYY">
                                                    {currentProject?.sale_opens_at}
                                                </Moment> : null
                                            }

                                        </div>
                                        <div className={Styles.dateBottom}>
                                            {currentProject?.sale_opens_at ? <>
                                                <Moment format="hh:mm A" utc>
                                                    {currentProject?.sale_opens_at}
                                                </Moment>{' '}
                                                - UTC
                                            </> : null}
                                        </div>
                                    </div>
                                </div>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img src={saleEndIcon} alt="Sale End Time" />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Время окончания продажи')}
                                        </div>
                                    </div>
                                    <div
                                        className={`${Styles.projectRowValueWrap} ${Styles.projectRowValueWrapWD}`}
                                    >
                                        <div className={Styles.dateTop}>
                                            {currentProject?.sale_ends_at ?
                                                <Moment format="MMMM Do YYYY">
                                                    {currentProject?.sale_ends_at}
                                                </Moment>
                                                : null}
                                        </div>
                                        <div className={Styles.dateBottom}>
                                            {currentProject?.sale_ends_at ? <>
                                                <Moment format="hh:mm A" utc>
                                                    {currentProject?.sale_ends_at}
                                                </Moment>{' '}
                                                - UTC
                                            </> : null}
                                        </div>
                                    </div>
                                </div>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img
                                                src={destributionIcon}
                                                alt="Token Distribution Time"
                                            />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Время распределения токенов')}
                                        </div>
                                    </div>
                                    <div
                                        className={`${Styles.projectRowValueWrap} ${Styles.projectRowValueWrapWD}`}
                                    >
                                        <div className={Styles.dateTop}>
                                            <Moment format="MMMM Do YYYY">
                                                {currentProject?.tge_time_opens_at}
                                            </Moment>
                                        </div>
                                        <div className={Styles.dateBottom}>
                                            <Moment format="hh:mm A" utc>
                                                {currentProject?.tge_time_opens_at}
                                            </Moment>{' '}
                                            - UTC
                                        </div>
                                    </div>
                                </div>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img src={marketIcon} alt="Initial Market Cap" />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Начальная капитализация')}
                                        </div>
                                    </div>
                                    <div className={Styles.projectRowValueWrap}>
                                        {currentProject?.initial_market_cap * 1} USDT
                                    </div>
                                </div>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img
                                                src={circulationIcon}
                                                alt="Initial Token Circulation"
                                            />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Первоначальный оборот')}
                                        </div>
                                    </div>
                                    <div className={Styles.projectRowValueWrap}>
                                        {currentProject?.initial_token_circulation * 1} {currentProject?.token}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB: ВЕСТИНГ */}
                    {currentTab === 'vesting' && (
                        <div className={`${Styles.projectWrap} ${Styles.vestingWrap}`}>
                            <div className={Styles.titleRow}>{t('Информация о вестинге')}</div>
                            <div className={Styles.projectBox}>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img src={vestingIcon} alt="Vesting Type" />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Тип вестинга')}
                                        </div>
                                    </div>
                                    <div className={Styles.projectRowValueWrap}>
                                        {currentProject?.vesting_type}
                                    </div>
                                </div>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img src={tgeDateIcon} alt="TGE Date" />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Дата выпуска')}
                                        </div>
                                    </div>
                                    <div
                                        className={`${Styles.projectRowValueWrap} ${Styles.projectRowValueWrapWD}`}
                                    >
                                        <div className={Styles.dateTop}>
                                            <Moment format="MMMM Do YYYY">
                                                {currentProject?.tge_time_opens_at}
                                            </Moment>
                                        </div>
                                        <div className={Styles.dateBottom}>
                                            <Moment format="hh:mm A" utc>
                                                {currentProject?.tge_time_opens_at}
                                            </Moment>{' '}
                                            - UTC
                                        </div>
                                    </div>
                                </div>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img src={tgeUnlockIcon} alt="TGE Unlock" />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Разблокировка TGE')} %
                                        </div>
                                    </div>
                                    <div className={Styles.projectRowValueWrap}>
                                        {currentProject?.tge_unlock_percent * 1}%
                                    </div>
                                </div>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img
                                                src={vestingStartIcon}
                                                alt="Linear Vesting Start"
                                            />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Начало линейного вестинга')}
                                        </div>
                                    </div>
                                    <div
                                        className={`${Styles.projectRowValueWrap} ${Styles.projectRowValueWrapWD}`}
                                    >
                                        <div className={Styles.dateTop}>
                                            <Moment format="MMMM Do YYYY">
                                                {currentProject?.linear_vesting_time_opens_at}
                                            </Moment>
                                        </div>
                                        <div className={Styles.dateBottom}>
                                            <Moment format="hh:mm A" utc>
                                                {currentProject?.linear_vesting_time_opens_at}
                                            </Moment>{' '}
                                            - UTC
                                        </div>
                                    </div>
                                </div>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img src={vestingEndIcon} alt="Linear Vesting End" />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Конец линейного вестинга')}
                                        </div>
                                    </div>
                                    <div
                                        className={`${Styles.projectRowValueWrap} ${Styles.projectRowValueWrapWD}`}
                                    >
                                        <div className={Styles.dateTop}>
                                            <Moment format="MMMM Do YYYY">
                                                {currentProject?.linear_vesting_time_ends_at}
                                            </Moment>
                                        </div>
                                        <div className={Styles.dateBottom}>
                                            <Moment format="hh:mm A" utc>
                                                {currentProject?.linear_vesting_time_ends_at}
                                            </Moment>{' '}
                                            - UTC
                                        </div>
                                    </div>
                                </div>
                                <div className={Styles.projectRow}>
                                    <div className={Styles.projectRowNameWrap}>
                                        <div className={Styles.projectRowNameIconWrap}>
                                            <img src={linearUnlockIcon} alt="Linear Unlock" />
                                        </div>
                                        <div className={Styles.projectRowName}>
                                            {t('Линейная разблокировка')} %
                                        </div>
                                    </div>
                                    <div className={Styles.projectRowValueWrap}>
                                        {currentProject?.linear_unlock_percent * 1}%
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default CabinetProjectDetails;




